/**
 * Pending API Types
 * Type definitions for pending results and validation operations.
 */

import type { LisSample, LisVisit, PendingFilter as ApiPendingFilter, Result } from "@skyware-group/iris-api-model";

/**
 * Represents a pending sample with basic information.
 * @property {string} barcode - Sample barcode identifier.
 * @property {boolean} isControl - Whether this is a control sample.
 * @property {string} [typeCode] - Optional sample type code (e.g., "EDTA", "СЕРУМ").
 * @property {string} [typeName] - Optional sample type name.
 * @property {string} [visitId] - Optional associated visit ID.
 * @property {string} [taken] - Optional sample collection timestamp in ISO 8601 format.
 * @property {SampleTypeColor} [color] - Optional RGBA color for sample type.
 */
export type PendingSample = LisSample;

/**
 * Result type enum for dynamic form rendering.
 */
export type ResultType = "numeric" | "enum" | "text";

/**
 * Edit history entry for result changes.
 * @property {string} originalValue - Original result value before edit.
 * @property {string} newValue - New result value after edit.
 * @property {string} editedBy - User who made the edit.
 * @property {string} editedAt - Timestamp when edit was made (ISO 8601).
 * @property {string} [reason] - Optional reason/justification for the edit.
 */
export interface ResultEditHistoryEntry {
  originalValue: string;
  newValue: string;
  editedBy: string;
  editedAt: string;
  reason?: string;
}

export interface PendingResultUiState {
  resultType?: ResultType;
  allowedValues?: string[];
  minValue?: number;
  maxValue?: number;
  isEdited?: boolean;
  editHistory?: ResultEditHistoryEntry[];
}

/**
 * Represents a pending result awaiting validation.
 * @property {number} id - Unique result identifier.
 * @property {string} resultTime - Result timestamp in ISO 8601 format with timezone.
 * @property {string} localResultTime - Result timestamp in local timezone.
 * @property {number} resultsSetId - Batch/set identifier for results.
 * @property {string} testCode - Test code from instrument.
 * @property {string} rawText - Raw text result from instrument.
 * @property {number} rawNumeric - Raw numeric result from instrument.
 * @property {string} textResult - Processed text result.
 * @property {number} numericResult - Processed numeric result.
 * @property {string} displayResult - Formatted result for display.
 * @property {string} mappingKey - Mapping key to LIS test identifier.
 * @property {string} lisName - Test name from LIS (if mapped).
 * @property {boolean} isAutoValidated - Whether result was auto-validated.
 * @property {ResultType} [resultType] - Type of result for dynamic editing (numeric/enum/text).
 * @property {string[]} [allowedValues] - Allowed values for enum type results.
 * @property {number} [minValue] - Minimum value for numeric results (reference range).
 * @property {number} [maxValue] - Maximum value for numeric results (reference range).
 * @property {boolean} [isEdited] - Whether this result has been manually edited.
 * @property {ResultEditHistoryEntry[]} [editHistory] - History of edits made to this result.
 *
 * v0.8.0-alpha.1 additive fields (all optional; legacy validation page consumers ignore them):
 * @property {string | null} [modifier] - Test modifier (e.g. for differentiating reflex tests).
 * @property {string | null} [testName] - Display name of the test.
 * @property {string | null} [units] - Unit of measure for the numeric result.
 * @property {string | null} [numericPrefix] - Comparator prefix such as "<", ">=" applied to numericResult.
 * @property {ResultFlag[]} [flags] - v0.8 structured flag list (parallel to legacy FlagItem usage).
 * @property {string | null} [validationTime] - UTC ISO timestamp when result was validated.
 * @property {string | null} [localValidationTime] - Local ISO timestamp when result was validated.
 * @property {string | null} [rejectionTime] - UTC ISO timestamp when result was rejected.
 * @property {string | null} [localRejectionTime] - Local ISO timestamp when result was rejected.
 */
export type PendingResult = Result & PendingResultUiState;

/**
 * Represents a pending visit with basic information.
 * @property {string} id - Visit identifier.
 * @property {string} visitDate - Visit date and time in ISO 8601 format.
 * @property {string} patientId - Patient identifier.
 * @property {string} givenName - Patient's given name.
 * @property {string} middleName - Patient's middle name.
 * @property {string} familyName - Patient's family name.
 * @property {string} dateOfBirth - Patient's date of birth in ISO 8601 format.
 * @property {boolean} isMale - Patient gender (true = male, false = female).
 * @property {number} numberOfSamples - Count of samples associated with visit.
 * @property {boolean} isStat - Whether this is a STAT (urgent) order.
 */
export type PendingVisit = LisVisit;

/**
 * Query parameters for filtering pending samples/visits.
 * @property {boolean} [filterUnknown] - Filter out samples unknown to Adapter.
 * @property {string} [locations] - Comma-separated location IDs.
 * @property {string} [instruments] - Comma-separated instrument IDs.
 * @property {string} [sampleTypes] - Comma-separated sample type IDs.
 * @property {string} [visitTypes] - Comma-separated visit type IDs.
 * @property {string} [requesters] - Comma-separated requester IDs.
 * @property {string} [doctors] - Comma-separated doctor IDs.
 * @property {boolean} [stat] - Filter by STAT status.
 * @property {number} [maxRows] - Maximum number of rows to return.
 */
export interface PendingFilters extends Partial<ApiPendingFilter> {
  filterUnknown?: boolean;
}

/**
 * Draft filter state (uses arrays for easier UI binding)
 */
export interface PendingFiltersDraft {
  locationIds?: number[];
  instrumentIds?: number[];
  sampleTypeIds?: number[];
  visitTypeIds?: number[];
  requesterIds?: number[];
  doctorIds?: number[];
  stat?: boolean;
  maxRows?: number;
}

/**
 * Result flag severity levels used by validation UI.
 */
export enum ResultFlagLevel {
  Unknown = 0,
  Information = 1,
  Warning = 2,
  Danger = 3,
  Panic = 4,
}

/**
 * Flag source types
 */
export enum FlagSource {
  Instrument = "Instrument",
  Processor = "Processor",
}

/**
 * Individual flag item
 */
export interface FlagItem {
  level: ResultFlagLevel;
  source: FlagSource;
  code: string;
  description: string;
}
